/**
 * AiClientSummary.js -- the AI Client Summary dialog.
 *
 * Opened from the robot/sparkle icon on the client Overview. Backed by
 * FWAPI helpers/clientSummary.js; prompt of record is
 * FWAPI/fieldworker/prompts/05-ai-client-summary.md.
 *
 * The summary is persisted server-side, so opening this dialog only READS --
 * it never spends a model call. Generating is always a deliberate click, which
 * is why there is a distinct empty state rather than an automatic fetch-and-
 * generate on mount.
 *
 * Rendered with structured MUI components rather than markdown. The model
 * returns typed sections, and the two markdown surfaces in this app
 * (AiCompanion, ElliQChat) have no sanitizer and no remark-gfm; the precedent
 * for non-chat model output is GuidanceWindow and ComplianceDetails, both
 * structured.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, IconButton, Button, Typography, Box,
    Chip, CircularProgress, Divider, Accordion, AccordionSummary, AccordionDetails, Tooltip, Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import axios from 'axios';
import moment from 'moment';
import { APIURL } from '../../config';
import { downloadSummaryPdf, administrationStats } from './summaryPdf';

const CONFIDENCE_COLORS = { high: 'success', medium: 'warning', low: 'error' };

/* The reducer reports why each bucket was held back using machine-readable
   codes. Map them to plain language -- never render the raw code to a user. */
const REASON_LABELS = {
    note_narration_cap: 'Only a sample of case notes was read in full',
    form_narration_cap: 'Only the most recent form submissions were read in full',
    task_narration_cap: 'Only the most recent tasks were read in detail',
    incident_narration_cap: 'Only the most recent incidents were read in detail',
    dose_exception_cap: 'Only the most recent not-given doses and dose outcomes were read in detail',
};

/* The model names what it was unsure about as a path into the context it was
   given -- "case_notes.quality_score_avg". Precise for it to emit, but a dotted
   identifier is not something to put in front of a coordinator. Same rule as
   REASON_LABELS: translate it, never render the raw token. */
const FIELD_AREA_LABELS = {
    client: 'Client details',
    client_overview: 'Client details',
    goals: 'Goals',
    goals_and_progress: 'Goals',
    services: 'Services',
    services_delivered: 'Services',
    case_notes: 'Case notes',
    documentation_quality: 'Documentation',
    forms: 'Forms',
    assessments: 'Assessments',
    assessments_and_needs: 'Assessments and needs',
    needs: 'Needs',
    medications: 'Medications',
    medications_and_health: 'Medications and health',
    medication_administration: 'Medication administration',
    incidents: 'Incidents',
    vaccines: 'Vaccinations',
    open_items: 'Open items',
    watch_points: 'Watch points',
    data_gaps: 'Data gaps',
};

const GENERIC_SEGMENTS = new Set(['overview', 'items', 'bullets', 'text', 'context', 'recent', 'summary', 'total', 'current']);

const humanizeSegment = (segment) => {
    const cleaned = String(segment || '')
        .replace(/_pct$/, '').replace(/^overall_/, '').replace(/[_.]+/g, ' ').trim();
    if (!cleaned) return null;
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
};

const fieldPathLabel = (fieldPath) => {
    const raw = String(fieldPath || '').trim();
    if (!raw) return null;
    if (!raw.includes('.') && raw.includes(' ')) return raw;
    // Array positions ("bullets[0]", "bullets.0") say which item, not what it is.
    const segments = raw.split('.').map((s) => s.replace(/\[\d+\]/g, '')).filter((s) => s && !/^\d+$/.test(s));
    if (!segments.length) return null;
    const area = FIELD_AREA_LABELS[segments[0]];
    const last = segments.length > 1 ? segments[segments.length - 1] : null;
    const leaf = last && !GENERIC_SEGMENTS.has(last) ? humanizeSegment(last) : null;
    if (area && leaf) return `${area} · ${leaf}`;
    if (area) return area;
    return humanizeSegment(segments[segments.length - 1]);
};

/* Citation chips. The server resolved each ref to a label already and dropped
   any the model invented, so whatever arrives here is a real record. Not
   clickable in v1: the client sub-panes are tab indices held in DetailsPane
   state, not routes, so deep-linking is separate navigation plumbing. The label
   is enough to find the record by hand. */
const SourceChips = ({ sources, unsourced }) => {
    if (unsourced) {
        return (
            <Typography variant="caption" sx={{ color: 'warning.main', fontStyle: 'italic', ml: 2.5 }}>
                No source recorded
            </Typography>
        );
    }
    if (!sources || sources.length === 0) return null;
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, ml: 2.5, mt: 0.5 }}>
            {sources.map((s, i) => (
                <Tooltip key={s.ref || i} title={s.ref || ''}>
                    <Chip label={s.label || s.ref} size="small" variant="outlined"
                        sx={{ height: 20, fontSize: 10 }} />
                </Tooltip>
            ))}
        </Box>
    );
};

const Bullet = ({ bullet }) => {
    if (!bullet) return null;
    const text = typeof bullet === 'string' ? bullet : bullet.text;
    if (!text) return null;
    return (
        <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex' }}>
                <Typography variant="body2" sx={{ mr: 1 }}>{'•'}</Typography>
                <Typography variant="body2" sx={{ flex: 1 }}>{text}</Typography>
            </Box>
            {typeof bullet === 'object' &&
                <SourceChips sources={bullet.sources} unsourced={bullet.unsourced} />}
        </Box>
    );
};

/* Renders nothing at all when the model returned no rows, so a sparse summary
   stays short instead of showing ten empty headers. */
const Section = ({ title, headline, bullets, extra }) => {
    const rows = (bullets || []).filter(Boolean);
    if (!rows.length && !headline && !extra) return null;
    return (
        <Box sx={{ mb: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main', letterSpacing: 0.4 }}>
                {title}
            </Typography>
            <Divider sx={{ mb: 1 }} />
            {headline && <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>{headline}</Typography>}
            {rows.map((b, i) => <Bullet key={i} bullet={b} />)}
            {extra}
        </Box>
    );
};

/* Label/value figures as a two-column table sized to its content, so each value
   sits next to its label instead of across the dialog. The shaded (section) table
   is indented to the bullet text, like the source chips, so it doesn't line up
   with the bullet points above it. */
const StatBox = ({ children, shaded = true }) => (
    <Box sx={{
        display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 3, rowGap: 0.5,
        width: 'fit-content', maxWidth: '100%',
        ...(shaded && { bgcolor: 'action.hover', borderRadius: 1, p: 1, mt: 1, ml: 2.5, maxWidth: 'calc(100% - 20px)' }),
    }}>
        {children}
    </Box>
);

const StatLine = ({ label, value }) => {
    if (value === null || value === undefined) return null;
    return (
        <>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}</Typography>
            <Typography variant="caption" sx={{ fontWeight: 700, textAlign: 'right' }}>{value}</Typography>
        </>
    );
};

const AiClientSummary = ({ open, onClose, cusId, customerName, user }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState(null);
    const [retryAfter, setRetryAfter] = useState(null);
    const [payload, setPayload] = useState(null);
    const [lastAttempt, setLastAttempt] = useState(null);
    // True while a full-year (uncapped) generation is running, for its own wait message.
    const [generatingUncapped, setGeneratingUncapped] = useState(false);

    const empId = user && user.emp_id;
    // The API allows a full-year summary for Admins only; same check as the Overview icon.
    const isAdmin = !!user && user.type === 'Admin';

    const readError = (err, fallback) => {
        const status = err.response && err.response.status;
        if (status === 403) return 'The AI Companion module is not enabled for this company.';
        if (status === 404) return 'This client is not on your caseload.';
        return (err.response && err.response.data && err.response.data.message) || fallback;
    };

    /* Read the cached summary. Cheap -- the server never calls the model here. */
    const fetchCached = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        setRetryAfter(null);
        try {
            const res = await axios.get(APIURL + '/client-summary', {
                params: { cus_id: cusId, emp_id: empId },
            });
            setPayload(res.data && res.data.summary ? res.data : null);
            setLastAttempt((res.data && res.data.last_attempt) || null);
        } catch (err) {
            console.log(err);
            setError(readError(err, 'Could not load the summary. Please try again.'));
        }
        setIsLoading(false);
    }, [cusId, empId]);

    /* Generate or re-generate. The call that costs a model round trip.
       `uncapped` (Admins only) reads every record in the window in full, with no
       sampling -- far more tokens, and much slower on a busy client. */
    const generate = useCallback(async (trigger, { uncapped = false } = {}) => {
        setIsGenerating(true);
        setGeneratingUncapped(uncapped);
        setError(null);
        setRetryAfter(null);
        try {
            const res = await axios.post(APIURL + '/client-summary/generate', {
                cus_id: cusId, empId, trigger, ...(uncapped ? { uncapped: true } : {}),
            });
            setPayload(res.data && res.data.summary ? res.data : null);
            setLastAttempt(null);
        } catch (err) {
            console.log(err);
            const status = err.response && err.response.status;
            // Foundry sheds load as a 503 with Retry-After; tell the user how
            // long rather than showing a generic failure.
            if (status === 503) {
                setRetryAfter((err.response.data && err.response.data.retryAfterSeconds) || null);
                setError('The AI service is busy right now.');
            } else if (status === 403 && uncapped) {
                // The Admin-only refusal, not the subscription one readError assumes for a 403.
                setError((err.response.data && err.response.data.message) || 'A full-year summary is available to Admins only.');
            } else {
                setError(readError(err, 'Could not generate the summary. Please try again.'));
            }
        }
        setIsGenerating(false);
        setGeneratingUncapped(false);
    }, [cusId, empId]);

    useEffect(() => {
        if (open && cusId) fetchCached();
        if (!open) { setPayload(null); setError(null); setLastAttempt(null); }
    }, [open, cusId, fetchCached]);

    const summary = (payload && payload.summary) || null;
    const aggregates = (payload && payload.aggregates) || {};
    const suppression = aggregates.suppression || {};
    const confidence = payload && payload.hitl && payload.hitl.document_confidence;
    const lowConfidenceFields = (summary && summary.low_confidence_fields) || [];

    const downloadPdf = () => {
        try {
            downloadSummaryPdf(payload, {
                customerName,
                printedBy: (user && (user.emp_name || [user.emp_first_name, user.emp_last_name].filter(Boolean).join(' '))) || '',
                printedAt: new Date(),
                fieldPathLabel,
                reasonLabels: REASON_LABELS,
            });
        } catch (err) {
            console.log(err);
            setError('Could not create the PDF. Please try again.');
        }
    };

    const renderBody = () => {
        if (isGenerating) {
            return (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }}>
                        {generatingUncapped ? 'Generating full-year summary…' : 'Generating summary…'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {generatingUncapped
                            ? 'This reads every record from the last 12 months in full and can take several minutes.'
                            : 'This reads the last 12 months and can take up to a minute.'}
                    </Typography>
                </Box>
            );
        }

        if (isLoading) {
            return (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }}>Loading summary…</Typography>
                </Box>
            );
        }

        if (error) {
            return (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <ErrorOutlineIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                    <Typography sx={{ mt: 1 }}>{error}</Typography>
                    {retryAfter &&
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                            Try again in about {retryAfter} seconds.
                        </Typography>}
                    <Button variant="contained" sx={{ mt: 2 }} onClick={fetchCached}>Try again</Button>
                </Box>
            );
        }

        /* Never generated. Distinct from an error: nobody has asked yet. */
        if (!summary) {
            return (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <AutoAwesomeIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                    <Typography sx={{ mt: 1 }}>No summary has been generated yet.</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 480, mx: 'auto', mt: 0.5 }}>
                        This reads the last 12 months of this client&apos;s record — notes, tasks, forms,
                        goals and assessments — and writes a plain-language summary. It can take up to a minute.
                    </Typography>
                    {lastAttempt && lastAttempt.status === 'failed' &&
                        <Typography variant="caption" sx={{ display: 'block', color: 'warning.main', mt: 1 }}>
                            The last attempt did not finish.
                        </Typography>}
                    <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Button variant="contained" onClick={() => generate('initial')}>
                            Generate summary
                        </Button>
                        {isAdmin &&
                            <Tooltip title="Reads every record from the last 12 months in full instead of a sample. Slower, and uses far more AI capacity.">
                                <Button variant="outlined" startIcon={<ManageSearchIcon />}
                                    onClick={() => generate('initial', { uncapped: true })}>
                                    Generate full-year summary
                                </Button>
                            </Tooltip>}
                    </Box>
                </Box>
            );
        }

        const overview = summary.client_overview || {};
        const meta = [
            `Generated ${moment(payload.generated_at).format('MM/DD/YYYY, hh:mm A')}`,
            payload.generated_by_name ? `by ${payload.generated_by_name}` : null,
        ].filter(Boolean).join(' ');

        return (
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{meta}</Typography>
                        {payload.window && payload.window.months &&
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                Covers the last {payload.window.months} months
                                {suppression.uncapped ? ' · every record read in full' : ''}
                            </Typography>}
                    </Box>
                    {suppression.uncapped &&
                        <Chip size="small" label="Full year" color="primary" variant="outlined" sx={{ mr: 1 }} />}
                    {confidence &&
                        <Chip size="small" label={`Confidence: ${confidence}`}
                            color={CONFIDENCE_COLORS[confidence] || 'default'} variant="outlined" />}
                </Box>

                {payload.is_stale &&
                    <Typography variant="caption" sx={{ display: 'block', color: 'warning.main', mb: 1.5 }}>
                        This summary is more than a week old. The record may have changed since.{' '}
                        {/* Same action as the header's re-generate icon. The body swaps to the
                            spinner while generating, so this can't be pressed twice. */}
                        <Link component="button" variant="caption" onClick={() => generate('regenerate')}
                            sx={{ verticalAlign: 'baseline', fontWeight: 600 }}>
                            Do you want to regenerate?
                        </Link>
                    </Typography>}

                {overview.summary &&
                    <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5, mb: 2.5 }}>
                        <Typography variant="body2">{overview.summary}</Typography>
                    </Box>}

                <Section title="KEY NEEDS" bullets={overview.key_needs} />
                <Section title="GOALS & PROGRESS"
                    headline={(summary.goals_and_progress || {}).headline}
                    bullets={(summary.goals_and_progress || {}).bullets} />
                <Section title="SERVICES DELIVERED"
                    headline={(summary.services_delivered || {}).headline}
                    bullets={(summary.services_delivered || {}).bullets}
                    extra={aggregates.task_overview && aggregates.task_overview.total ? (
                        <StatBox>
                            <StatLine label="Tasks in window" value={aggregates.task_overview.total} />
                            <StatLine label="Completed" value={aggregates.task_overview.completed} />
                            <StatLine label="Open" value={aggregates.task_overview.open} />
                        </StatBox>
                    ) : null} />
                <Section title="ASSESSMENTS & NEEDS" bullets={(summary.assessments_and_needs || {}).bullets} />
                <Section title="MEDICATIONS & HEALTH"
                    headline={(summary.medications_and_health || {}).headline}
                    bullets={(summary.medications_and_health || {}).bullets}
                    extra={administrationStats(aggregates.medication_administration) ? (
                        <StatBox>
                            {administrationStats(aggregates.medication_administration).map(([label, value]) => (
                                <StatLine key={label} label={label} value={value} />
                            ))}
                        </StatBox>
                    ) : null} />
                <Section title="DOCUMENTATION"
                    headline={(summary.documentation_quality || {}).headline}
                    bullets={(summary.documentation_quality || {}).bullets}
                    extra={aggregates.note_overview && aggregates.note_overview.total ? (
                        <StatBox>
                            <StatLine label="Case notes" value={aggregates.note_overview.total} />
                            <StatLine label="Quality-scored" value={aggregates.note_overview.quality_scored_count} />
                            <StatLine label="Average score" value={aggregates.note_overview.quality_score_avg} />
                        </StatBox>
                    ) : null} />
                <Section title="OPEN ITEMS" bullets={summary.open_items} />
                <Section title="WATCH POINTS" bullets={summary.watch_points} />
                <Section title="QUESTIONS FOR THE TEAM" bullets={summary.questions_for_the_team} />

                {lowConfidenceFields.length > 0 &&
                    <Accordion defaultExpanded disableGutters elevation={0} sx={{ mt: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                Verify these ({lowConfidenceFields.length})
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {lowConfidenceFields.map((f, i) => {
                                const label = fieldPathLabel(f.field_path);
                                return (
                                    <Box key={i} sx={{ mb: 1 }}>
                                        {label && <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>}
                                        <Typography variant={label ? 'caption' : 'body2'} sx={{ color: 'text.secondary' }}>
                                            {f.issue}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </AccordionDetails>
                    </Accordion>}

                {(summary.data_gaps || []).length > 0 &&
                    <Accordion disableGutters elevation={0}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                Data gaps
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {(summary.data_gaps || []).map((g, i) => <Bullet key={i} bullet={g} />)}
                        </AccordionDetails>
                    </Accordion>}

                {/* The honest counterpart to the citation chips: citations say
                    where a claim came from, this says how much was never read. */}
                {suppression.notes &&
                    <Accordion disableGutters elevation={0}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                What the summary is based on
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            {suppression.window &&
                                <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                                    {suppression.window.start} – {suppression.window.end}
                                </Typography>}
                            <StatBox shaded={false}>
                                <StatLine label="Case notes read in full"
                                    value={`${suppression.notes.narrated} of ${suppression.notes.total}`} />
                                {suppression.forms &&
                                    <StatLine label="Forms read in full"
                                        value={`${suppression.forms.narrated} of ${suppression.forms.total}`} />}
                                {suppression.tasks &&
                                    <StatLine label="Tasks read in detail"
                                        value={`${suppression.tasks.narrated} of ${suppression.tasks.total}`} />}
                            </StatBox>
                            {[suppression.notes, suppression.forms, suppression.tasks, suppression.incidents, suppression.medication_administration]
                                .filter(Boolean)
                                .map((b) => REASON_LABELS[b.suppression_reason])
                                .filter(Boolean)
                                .map((label, i) => (
                                    <Typography key={i} variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                                        {label}
                                    </Typography>
                                ))}
                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1 }}>
                                {suppression.uncapped
                                    ? 'Full-year summary: every record in the window was read in full.'
                                    : 'Everything else was counted, not read. The AI did not see those records.'}
                            </Typography>
                        </AccordionDetails>
                    </Accordion>}
            </Box>
        );
    };

    return (
        <Dialog fullWidth maxWidth="md" open={!!open} onClose={onClose} scroll="paper">
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AutoAwesomeIcon fontSize="small" sx={{ color: '#3FA7D6' }} />
                    <Box>
                        <span>Annual Client Summary{customerName ? ` — ${customerName}` : ''}</span>
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.2 }}>
                            Generated by Fieldworker AI. Verify against the record before acting.
                        </Typography>
                    </Box>
                </Box>
                <Box>
                    {summary &&
                        <Tooltip title="Download as PDF">
                            <span>
                                <IconButton size="small" disabled={isGenerating || isLoading}
                                    onClick={downloadPdf}>
                                    <PictureAsPdfIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>}
                    {summary &&
                        <Tooltip title="Re-generate from the latest records">
                            <span>
                                <IconButton size="small" disabled={isGenerating || isLoading}
                                    onClick={() => generate('regenerate')}>
                                    <RefreshIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>}
                    {summary && isAdmin &&
                        <Tooltip title="Re-generate from every record in the last 12 months, read in full (Admins only). Slower, and uses far more AI capacity.">
                            <span>
                                <IconButton size="small" disabled={isGenerating || isLoading}
                                    onClick={() => generate('regenerate', { uncapped: true })}>
                                    <ManageSearchIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>}
                    <IconButton onClick={onClose}><CloseIcon /></IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers>{renderBody()}</DialogContent>
        </Dialog>
    );
};

export default AiClientSummary;
